/**
 * useCollapsedPills — width-driven pill collapse logic for CollapsingMultiSelect.
 *
 * Renders a hidden "measurer" carrying the FULL set of pills regardless of the
 * collapsed state, so its measured width is stable (no flip-flop): collapsing
 * only changes what the VISIBLE input shows, never what the measurer renders.
 * A `ResizeObserver` watches the visible pills container; whenever its available
 * width or the measurer width changes we recompute `collapsed`.
 *
 * The visible pills container is located by class inside the wrapper element
 * (Mantine's `MultiSelect` forwards its `ref` to the inner input, not the pills
 * list — so we cannot ref the list directly and resolve it via `querySelector`).
 *
 * NOT exported from the component barrel — internal to CollapsingMultiSelect.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Below this many selected values the pills always fit — never collapse. */
const MIN_VALUES_TO_COLLAPSE = 2;

interface UseCollapsedPillsResult {
  /** True when the full pill set overflows the visible container's single line. */
  collapsed: boolean;
  /** Ref for the wrapper that hosts the visible MultiSelect. */
  wrapperRef: (node: HTMLDivElement | null) => void;
  /** Ref for the hidden measurer that renders the full pill set on one line. */
  measurerRef: (node: HTMLDivElement | null) => void;
}

/**
 * @param selectedCount number of currently selected values (drives the guard + re-measure)
 * @param pillsContainerClassName class applied to the MultiSelect `pillsList` part,
 *        used to locate the visible pills container inside the wrapper
 */
export function useCollapsedPills(
  selectedCount: number,
  pillsContainerClassName: string
): UseCollapsedPillsResult {
  const [collapsed, setCollapsed] = useState(false);
  const wrapperNodeRef = useRef<HTMLDivElement | null>(null);
  const measurerNodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const resolveContainer = useCallback((): HTMLElement | null => {
    const wrapper = wrapperNodeRef.current;
    if (!wrapper || !pillsContainerClassName) {
      return null;
    }
    return wrapper.querySelector<HTMLElement>(`.${pillsContainerClassName}`);
  }, [pillsContainerClassName]);

  const recompute = useCallback(() => {
    if (selectedCount < MIN_VALUES_TO_COLLAPSE) {
      setCollapsed(false);
      return;
    }
    const container = resolveContainer();
    const measurer = measurerNodeRef.current;
    if (!container || !measurer) {
      return;
    }
    const availableWidth = container.clientWidth;
    if (availableWidth === 0) {
      // Container not laid out yet — wait for the ResizeObserver to re-fire with a real width.
      return;
    }
    const requiredWidth = measurer.scrollWidth;
    // Guard against the ResizeObserver feedback loop: only flip when the new
    // verdict actually differs from the current one.
    const shouldCollapse = requiredWidth > availableWidth;
    setCollapsed((wasCollapsed) =>
      wasCollapsed === shouldCollapse ? wasCollapsed : shouldCollapse
    );
  }, [resolveContainer, selectedCount]);

  // Re-measure when the selection (and therefore the pill set) changes.
  useEffect(() => {
    recompute();
  }, [recompute]);

  // Observe the wrapper so available-width changes re-measure the pills.
  const wrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      wrapperNodeRef.current = node;
      if (!node) {
        return;
      }
      const observer = new ResizeObserver(() => recompute());
      observer.observe(node);
      observerRef.current = observer;
      recompute();
    },
    [recompute]
  );

  const measurerRef = useCallback(
    (node: HTMLDivElement | null) => {
      measurerNodeRef.current = node;
      recompute();
    },
    [recompute]
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
    },
    []
  );

  return { collapsed, wrapperRef, measurerRef };
}
